import { Inline, Stack, vars, Text, textVariants } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import type { MouseEvent } from 'react';
import type { IncludedCheckedBaggage } from '@eti/schema-types';
import {
  getMinNumberOfSeats,
  shouldShowSeatsLeftNotification,
} from '../../../../utils/resultUtils';
import SeatsLeftInfo from '../../../../../../common/components/SeatsLeftInfo';
import type {
  AvailableExtraProductsType,
  BoundsType,
  IncludedCabinBaggageType,
} from '../../../../models';
import type {
  IncludedCabinBaggageType as RebookingTripIncludedCabinBaggageType,
  RebookingTripBoundsType,
} from '../../../../../change-trip/models';
import type { RebookingTicketProps, Ticket } from '../../drawer/model';
import { routes } from '../../../../../../constants/routesAndApisConstants';
import { tripHasSelfTransfer } from '../../utils/trip';
import { TripBaggageInfo } from './TripBaggageInfo';
import PriceSection from './PriceSection';

const tripBaggageAndPriceInfoWrapperStyles = css`
  border-top: 1px solid ${vars.colors.divider};
  cursor: pointer;
  justify-content: space-between;
  padding: 8px 16px;
`;

const selfTransferPromotionStyles = css`
  color: ${vars.colors.promotional.foreground};
`;

const wrapperStyles = css`
  width: 100%;
`;

const tripBaggageInfoMobileStyles = css`
  justify-content: space-between;
`;

export interface TripBaggageAndPriceInfoMobileProps {
  availableExtraProducts?: AvailableExtraProductsType;
  bounds: BoundsType | RebookingTripBoundsType;
  includedCabinBaggage: IncludedCabinBaggageType | RebookingTripIncludedCabinBaggageType;
  includedCheckedBaggage?: IncludedCheckedBaggage | null;
  isVI?: boolean;
  numberOfTravelers?: number;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  ticket: RebookingTicketProps | Ticket;
}

const TripBaggageAndPriceInfoMobile = ({
  availableExtraProducts = [],
  bounds,
  includedCabinBaggage,
  includedCheckedBaggage,
  isVI,
  numberOfTravelers,
  onClick,
  ticket,
}: TripBaggageAndPriceInfoMobileProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const isVISelfTransferDealEnabled: boolean = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const isSelfTransferInTrip = isVISelfTransferDealEnabled && tripHasSelfTransfer(bounds);
  const numberOfSeatsLeft = getMinNumberOfSeats(bounds);
  const seatsThreshold = p('Air.Result.ShowNumberOfSeatsLeft.WhenLessThan');
  const isChangeTripPage = window.location.pathname.includes(routes.CHANGE_TRIP);
  const shouldShowSeatsLeftNotificationMessage =
    !isChangeTripPage &&
    shouldShowSeatsLeftNotification(numberOfSeatsLeft, numberOfTravelers, seatsThreshold);
  const shouldRenderSeatsLeftInfo = shouldShowSeatsLeftNotificationMessage && !isSelfTransferInTrip;

  return (
    <Stack
      align="start"
      className={tripBaggageAndPriceInfoWrapperStyles}
      onClick={onClick}
      role={isSelfTransferInTrip ? 'button' : undefined}
      spacing={8}
    >
      <Stack className={wrapperStyles} spacing={8}>
        {(isSelfTransferInTrip || shouldRenderSeatsLeftInfo) && (
          <Inline align="end">
            {isSelfTransferInTrip && (
              <Text className={selfTransferPromotionStyles} variant={textVariants._14_BOLD}>
                {t('Result.Page.SelfTransferDeals.Promotion.Text')}
              </Text>
            )}
            {shouldRenderSeatsLeftInfo && <SeatsLeftInfo numberOfSeatsLeft={numberOfSeatsLeft} />}
          </Inline>
        )}
        <Inline alignY="top" className={tripBaggageInfoMobileStyles}>
          <TripBaggageInfo
            availableExtraProducts={availableExtraProducts}
            bounds={bounds}
            includedCabinBaggage={includedCabinBaggage}
            includedCheckedBaggage={includedCheckedBaggage}
            isVI={isVI}
          />

          <PriceSection
            priceDisplayText={ticket.priceDisplayText}
            pricePerPerson={ticket.pricePerPerson}
          />
        </Inline>
      </Stack>
    </Stack>
  );
};

export default TripBaggageAndPriceInfoMobile;
